/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import ReactPixel from 'react-facebook-pixel'
import { useTranslation } from 'react-i18next'
import { Linking } from 'react-native'
import TagManager from 'react-gtm-module'
import moment from 'moment'
import { faUser, faPen } from '@fortawesome/pro-light-svg-icons'
import { faTicket } from '@fortawesome/free-solid-svg-icons'

import { AbsoluteSpinner, BackButton, Logo, FaIcon } from '../App/Components'
import { StyledScrollView } from '../App/Components/AppContainer'
import { BookingSuccess } from '../App/Components/BookingSuccess/BookingSuccess'
import { setAppointmentStatus } from '../Appointment/store/Actions'
import { IMenuActivity } from '../Menu/Store/IMenuActivity'
import { Link, useHistory, useParams } from '../Router'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import Client from '../Appointment/Client/Client'
import { IUserData, setUserData } from '../App/Util'
import { bookingFormErrorHandling } from '../App/Util/bookingFormErrorHandling'
import { ApiAppointmentResponse } from '../Appointment/store/interface/IApiAppointmentResponse'
import { isNativeCheck } from '../App/Util/isNativeCheck'
import { isPreviewDeposit } from '../App/Util/isDepositCheck/isPreviewDeposit'
import { DownloadInfo } from '../App/types'
import {
  ServiceTitle,
  ConfirmBlock,
  ConfirmBlockHeader,
  ConfirmBlockContent,
  ConfirmServiceRow,
  ConfirmServiceText,
  ConfirmDetailText,
  ConfirmBlockEditIcon,
} from '../Appointment/style'
import { TotalPrice } from '../Appointment/Components/TotalPrice/TotalPrice'
import {
  ConfirmPageWrapper,
  ConfirmScrollArea,
  ConfirmButtonFooter,
  ConfirmBookingButton,
  LoginForPunchCardCheckButton,
} from './style'
import { DefaultButtonWithText } from '../App/Components/ButtonWithText/style'
import { isAuthenticated } from '../cookies/session.cookies'
import { encodeBookingState } from '../BookingRestore/encode'
import { customerPortalLinks } from '../App/Util/customerPortalUrl'
import {
  usePricePreview,
  ActivityPriceInfo,
} from '../PunchCard/usePricePreview'
import { PunchCardConfirmBlock } from '../PunchCard/Components/PunchCardConfirmBlock/PunchCardConfirmBlock'
import { PunchCardExpiredBlock } from '../PunchCard/Components/PunchCardExpiredBlock/PunchCardExpiredBlock'
import { useCustomerPunchCards } from '../PunchCard/useCustomerPunchCards'

interface ISuccessData {
  response: ApiAppointmentResponse
  downloadId?: number
  message?: string
  type: 'appointment'
  downloadInfo: DownloadInfo
}

export const AppointmentConfirm = () => {
  const [isLoading, setLoading] = useState<boolean>(false)
  const [successData, setSuccessData] = useState<ISuccessData | undefined>()
  const isNative = isNativeCheck()
  const inFrame = window.self !== window.top
  const { establishmentId } = useParams<{ establishmentId: string }>()
  const dispatch = useAppDispatch()
  const history = useHistory()
  const { t } = useTranslation()
  const pagePath = history.location.pathname

  const { appointment, settingsState } = useAppSelector((state) => ({
    appointment: state.appointment,
    settingsState: state.settings,
  }))

  const { customerFormData, customers, date, customerPunchCardUuids } =
    appointment

  const activities: IMenuActivity[] = useMemo(
    () => customers.map((a) => a.activities).flat(1),
    [customers]
  )

  const appointmentDate = date ? moment(date).format('YYYY-MM-DD') : undefined

  const previewWithDate = usePricePreview(
    settingsState.values?.uuid,
    establishmentId,
    customers,
    appointmentDate,
    customerPunchCardUuids
  )

  const previewWithoutDate = usePricePreview(
    settingsState.values?.uuid,
    establishmentId,
    customers,
    undefined,
    customerPunchCardUuids
  )

  const findPriceInfo = (
    activity: IMenuActivity
  ): ActivityPriceInfo | undefined =>
    previewWithDate.priceInfo.find(
      (info) =>
        (info.categoryId !== null && info.categoryId === activity.categoryId) ||
        (info.categoryId === null && info.activityId === activity.id)
    )

  const totalPrice = previewWithDate.totalPrice
  const totalDeposit = previewWithDate.totalDeposit
  const isDeposit = isPreviewDeposit(settingsState, previewWithDate.totalDeposit)

  const allCustomerCards = useCustomerPunchCards(
    settingsState.values?.uuid,
    establishmentId
  )

  const activeCards = allCustomerCards.filter((card) =>
    previewWithDate.punchCardUuids.includes(card.uuid)
  )

  const overdueUuids = previewWithoutDate.punchCardUuids.filter(
    (uuid) => !previewWithDate.punchCardUuids.includes(uuid)
  )
  const expiredCards = allCustomerCards.filter((card) =>
    overdueUuids.includes(card.uuid)
  )

  useEffect(() => {
    if (activities.length === 0) {
      history.push(`/${establishmentId}/appointment`)
      return
    }
    if (!date) {
      history.push(`/${establishmentId}/appointment/date`)
      return
    }
    if (!customerFormData) {
      history.push(`/${establishmentId}/appointment/book`)
    }
  }, [activities.length, date, customerFormData, history, establishmentId])

  const handleConfirm = useCallback(async () => {
    if (!customerFormData) return

    try {
      setLoading(true)

      const activitiesToSend: IMenuActivity[][] = customers.map(
        (customer) => customer.activities
      )

      const {
        firstName,
        lastName,
        phone,
        email,
        comment,
        sendEmail,
        customerPaysFullAmount,
        giftcardBarcode,
        street,
        city,
        zip,
      } = customerFormData

      const response = await Client.postAppointment(
        establishmentId,
        activitiesToSend,
        firstName,
        lastName,
        phone,
        email,
        comment,
        sendEmail,
        date,
        customerPaysFullAmount,
        giftcardBarcode,
        street,
        city,
        zip,
        customerPunchCardUuids
      )
      const storageObject: IUserData = {
        firstName,
        lastName,
        phone,
        email,
        isEmailSend: sendEmail,
        street,
        city,
        zip,
      }
      setUserData(storageObject)
      const mollieUrl = response.data.online_payment_url
      if (mollieUrl) {
        if (isNative) {
          if (!inFrame && (await Linking.canOpenURL(mollieUrl))) {
            await Linking.openURL(mollieUrl)
            dispatch(setAppointmentStatus('booking_success'))
            setSuccessData({
              downloadId: response.data.appointments[0].id,
              response: response.data,
              type: 'appointment',
              downloadInfo: {
                customerToken: response.data.token,
                appointmentUuid: response.data.appointments[0].uuid,
              },
            })
            setLoading(false)
            return
          }
          setSuccessData({
            message: 'You received a mail with a deposit payment request.',
            response: response.data,
            type: 'appointment',
            downloadInfo: {
              customerToken: response.data.token,
              appointmentUuid: response.data.appointments[0].uuid,
            },
          })
          setLoading(false)
          return
        }
        window.open(mollieUrl, '_self')
      }
      dispatch(setAppointmentStatus('booking_success'))
      setSuccessData({
        downloadId: response.data.appointments[0].id,
        response: response.data,
        type: 'appointment',
        downloadInfo: {
          customerToken: response.data.token,
          appointmentUuid: response.data.appointments[0].uuid,
        },
      })
      setLoading(false)
    } catch (error) {
      setLoading(false)
      bookingFormErrorHandling(
        establishmentId,
        customerFormData.email,
        error,
        dispatch,
        history,
        t
      )
    }
  }, [
    customerFormData,
    customers,
    date,
    dispatch,
    t,
    history,
    establishmentId,
    isNative,
    inFrame,
  ])

  if (successData) {
    TagManager.dataLayer({
      dataLayer: {
        event: 'booking_success',
        pagePath: pagePath,
        pageTitle: t('Booking success'),
      },
    })

    if (settingsState.values?.facebookPixel) {
      ReactPixel.pageView()
    }
    return (
      <StyledScrollView>
        <BookingSuccess
          responseType={successData}
          successMessage={successData.message}
          downloadInfo={successData.downloadInfo}
        />
      </StyledScrollView>
    )
  }

  if (isLoading || !settingsState.values || !customerFormData) {
    return (
      <StyledScrollView>
        <AbsoluteSpinner />
        {/* Spacer: AbsoluteSpinner is position:absolute and contributes no
            layout height, which would collapse the PoweredBy footer up
            against the top. Reserve 350px so the footer sits below the
            spinner. */}
        <div style={{ height: '350px' }} />
      </StyledScrollView>
    )
  }

  const { highlightColor, backgroundColor, showPrice, fontColor } =
    settingsState.values

  const formattedDate = date
    ? moment(date).format('dddd D MMMM YYYY, HH:mm')
    : ''

  return (
    <ConfirmPageWrapper>
      <ConfirmScrollArea>
        <Logo />
        <ServiceTitle fontcolor={fontColor}>
          {t('Confirm your appointment')}
        </ServiceTitle>
        <BackButton customPath={`/${establishmentId}/appointment/book`} />

        <ConfirmBlock highlightcolor={highlightColor}>
          <Link
            to={`/${establishmentId}/appointment`}
            style={{ textDecoration: 'none' }}
          >
            <ConfirmBlockEditIcon>
              <FaIcon size="sm" icon={faPen} color={highlightColor} />
            </ConfirmBlockEditIcon>
          </Link>
          <ConfirmBlockHeader fontcolor={fontColor}>
            {t('Services')}
          </ConfirmBlockHeader>
          <ConfirmBlockContent>
            {customers.map((customer, customerIndex) => (
              <React.Fragment key={`confirm-customer-${customerIndex}`}>
                {customer.activities.map((activity, activityIndex) => {
                  const priceInfo = findPriceInfo(activity)
                  const price =
                    activity.type === 'menuActivity'
                      ? activity.activityMenuPrice
                      : activity.price
                  const hasPunchCard = priceInfo?.punchCardUuid != null
                  const isFirstActivity = activityIndex === 0

                  const priceDisplay = hasPunchCard ? (
                    <FaIcon size="sm" icon={faTicket} color={fontColor} />
                  ) : showPrice && typeof price === 'number' && price > 0 ? (
                    ` €${price.toFixed(2)}`
                  ) : (
                    ''
                  )

                  if (isFirstActivity) {
                    return (
                      <ConfirmServiceRow
                        key={`confirm-activity-${activity.id}-${customerIndex}`}
                      >
                        <FaIcon
                          size="lg"
                          icon={faUser}
                          color={fontColor}
                          cssStyle={{ marginTop: '3px' }}
                        />
                        <ConfirmServiceText
                          fontcolor={fontColor}
                          data-component="confirm-detail-text"
                        >
                          <span>{t(`activities;;${activity.name}`)}</span>
                          <span>
                            {activity.personSelected
                              ? ` (${activity.personSelected.firstName})`
                              : ''}{' '}
                            {priceDisplay}
                          </span>
                        </ConfirmServiceText>
                      </ConfirmServiceRow>
                    )
                  }

                  return (
                    <ConfirmDetailText
                      data-component="confirm-detail-text"
                      key={`confirm-activity-${activity.id}-${customerIndex}`}
                      fontcolor={fontColor}
                      style={{ marginLeft: 32 }}
                    >
                      <span>{t(`activities;;${activity.name}`)}</span>
                      <span>
                        {activity.personSelected
                          ? ` (${activity.personSelected.firstName})`
                          : ''}{' '}
                        {priceDisplay}
                      </span>
                    </ConfirmDetailText>
                  )
                })}
              </React.Fragment>
            ))}
          </ConfirmBlockContent>
        </ConfirmBlock>

        <ConfirmBlock highlightcolor={highlightColor}>
          <Link
            to={`/${establishmentId}/appointment/date`}
            style={{ textDecoration: 'none' }}
          >
            <ConfirmBlockEditIcon>
              <FaIcon size="sm" icon={faPen} color={highlightColor} />
            </ConfirmBlockEditIcon>
          </Link>
          <ConfirmBlockHeader fontcolor={fontColor}>
            {t('Date & time')}
          </ConfirmBlockHeader>
          <ConfirmBlockContent>
            <ConfirmDetailText fontcolor={fontColor}>
              {formattedDate}
            </ConfirmDetailText>
          </ConfirmBlockContent>
        </ConfirmBlock>

        <ConfirmBlock highlightcolor={highlightColor}>
          <Link
            to={`/${establishmentId}/appointment/book`}
            style={{ textDecoration: 'none' }}
          >
            <ConfirmBlockEditIcon>
              <FaIcon size="sm" icon={faPen} color={highlightColor} />
            </ConfirmBlockEditIcon>
          </Link>
          <ConfirmBlockHeader fontcolor={fontColor}>
            {t('Your details')}
          </ConfirmBlockHeader>
          <ConfirmBlockContent>
            <ConfirmDetailText fontcolor={fontColor}>
              {`${customerFormData.firstName} ${customerFormData.lastName}`}
            </ConfirmDetailText>
            <ConfirmDetailText fontcolor={fontColor}>
              {customerFormData.email}
            </ConfirmDetailText>
            <ConfirmDetailText fontcolor={fontColor}>
              {customerFormData.phone}
            </ConfirmDetailText>
            {customerFormData.street ? (
              <ConfirmDetailText fontcolor={fontColor}>
                {`${customerFormData.street}${
                  customerFormData.zip ? `, ${customerFormData.zip}` : ''
                }${customerFormData.city ? ` ${customerFormData.city}` : ''}`}
              </ConfirmDetailText>
            ) : null}
            {customerFormData.comment ? (
              <ConfirmDetailText fontcolor={fontColor}>
                {customerFormData.comment}
              </ConfirmDetailText>
            ) : null}
          </ConfirmBlockContent>
        </ConfirmBlock>

        <PunchCardConfirmBlock
          cards={activeCards}
          highlightColor={highlightColor}
          fontColor={fontColor}
        />

        <PunchCardExpiredBlock
          cards={expiredCards}
          highlightColor={highlightColor}
          fontColor={fontColor}
        />

        <TotalPrice
          totalPrice={totalPrice}
          isShowPrice={showPrice}
          totalDeposit={totalDeposit}
          isShowDeposit={isDeposit}
        />
      </ConfirmScrollArea>

      <ConfirmButtonFooter>
        {!isAuthenticated(establishmentId) &&
          settingsState.values.onlinePunchCardSaleEnabled && (
            <LoginForPunchCardCheckButton
              backgroundcolor="transparent"
              fontcolor={fontColor}
              customMargin="0 10px 8px"
              onPress={() => {
                const restore = encodeBookingState(appointment)
                const returnTo = `${window.location.origin}/${establishmentId}?restore=${restore}`
                Linking.openURL(
                  customerPortalLinks.login(establishmentId, returnTo)
                )
              }}
            >
              {t('Login to check for Punch Cards')}
            </LoginForPunchCardCheckButton>
          )}
        <ConfirmBookingButton
          backgroundcolor={highlightColor}
          fontcolor={backgroundColor}
          customMargin="0"
          onPress={handleConfirm}
        >
          {t('Confirm booking')}
        </ConfirmBookingButton>
      </ConfirmButtonFooter>
    </ConfirmPageWrapper>
  )
}
